import { Box, Grid, Typography, TextField, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ConfigEntryType, ConfigType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { useState, KeyboardEvent } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import RenderCompanyConfigEntryItem from './RenderCompanyConfigEntryItem';
import { useConfigQuery, ConfigAPI } from '@buildwise/libs/configurations';

type RenderCompanyConfigProps = {
  config: ConfigType;
};

function RenderCompanyConfig({ config }: RenderCompanyConfigProps) {
  const { companyId, isNovaAdmin } = useUserData();
  const [value, setValue] = useState<string>('');
  const { updateConfigCache, configs } = useConfigQuery({
    companyId,
  });

  const addConfig = async (
    configArray: ConfigEntryType[],
    entryCode: string,
    callback: (value: string) => void,
  ) => {
    try {
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        entries: [
          ...configArray,
          {
            entryCode: entryCode,
            entryDescription: entryCode,
            isActive: true,
            entryValues: [],
          },
        ],
      });
      toast.success(`${entryCode} added successfully`);
      updateConfigCache({
        companyId,
        updatedConfig: {
          configs: configs?.map((config) =>
            config?.configName === data?.config?.configName ? data?.config : config,
          ),
        },
      });
      callback('');
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const updateConfigEntriesOrder = async (entries: ConfigEntryType[], configName: string) => {
    try {
      updateConfigCache({
        companyId,
        updatedConfig: {
          configs: configs.map((config) =>
            config?.configName === configName ? { ...config, entries } : config,
          ),
        },
      });
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        companyId,
        sortFlag: false,
        entries,
      });
      updateConfigCache({
        companyId,
        updatedConfig: {
          configs: configs?.map((config) =>
            config?.configName === data?.config?.configName ? data?.config : config,
          ),
        },
      });
    } catch (e) {
      updateConfigCache({
        companyId,
        updatedConfig: {
          configs,
        },
      });
      toast.error(catchErrorMessage(e));
    }
  };

  const handleEnter = (
    e: KeyboardEvent<HTMLDivElement>,
    configArray: ConfigEntryType[],
    configName = '',
    description = '',
    entryCode = '',
    callback: (value: string) => void,
  ) => {
    if (e.key === 'Enter') {
      addConfig(configArray, entryCode, callback);
    }
  };

  return (
    <Container id={config.configName}>
      <Box sx={{ padding: 4 }}>
        <Grid container spacing={2}>
          <Grid item md={3} xs={2} sx={{ paddingRight: 4 }}>
            <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
              {config.description || config.configName}
            </Typography>
          </Grid>
          <Grid container item md={9} xs={12}>
            {isNovaAdmin && (
              <Grid container item md={12} sx={{ marginBottom: 2 }}>
                <Grid item md={10}>
                  <TextField
                    placeholder="Enter text here..."
                    value={value}
                    onKeyUp={(e) => {
                      handleEnter(
                        e,
                        config.entries,
                        config?.configName,
                        config?.description,
                        value,
                        setValue,
                      );
                    }}
                    onChange={(event) => setValue(event?.target.value)}
                    type="text"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item md={2}>
                  <Button
                    sx={{ ml: 2 }}
                    variant="outlined"
                    disabled={!value}
                    onClick={() => {
                      addConfig(config.entries, value, setValue);
                    }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            )}
            <Grid container item md={12}>
              <DndContext
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over?.id) {
                    const activeIndex = config.entries.findIndex(({ id }) => id === active.id);
                    const overIndex = config.entries.findIndex(({ id }) => id === over.id);

                    updateConfigEntriesOrder(
                      arrayMove(config.entries, activeIndex, overIndex),
                      config?.configName,
                    );
                  }
                }}
              >
                <SortableContext items={config.entries.map((entry) => ({ id: entry?.id || '' }))}>
                  {config.entries.map((entry, index) => (
                    <RenderCompanyConfigEntryItem
                      key={entry.id}
                      config={config}
                      entry={entry}
                      index={index}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

const Container = styled(Paper, { label: 'Container' })(({ theme }) => ({
  background: theme.palette.custom.white,
  borderRadius: 16,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  marginBottom: 24,
  overflow: 'hidden',
  marginLeft: 12,
}));

export default RenderCompanyConfig;
